<template>
  <div class="flex flex-col space-y-5 bg-white p-8 shadow-xs">
    <div class="relative flex justify-between text-left">
      <div class="flex w-full items-center justify-between">
        <div class="flex items-center space-x-4">
          <back-button :route="backRoute" />
          <div>
            <lf-heading1>
              <template v-if="activeTemplate?.name">
                {{
                  $t("CONTRACTS.CONTRACT_BUILDER.EDIT_TITLE", {
                    name: activeTemplate.name,
                  })
                }}
              </template>
              <template v-else>
                {{ $t("CONTRACTS.CONTRACT_BUILDER.CREATE_CONTRACT") }}
              </template>
            </lf-heading1>
          </div>
        </div>
        <div class="flex items-center space-x-4">
          <template-action-buttons
            class="flex items-stretch space-x-4"
            :is-edit="isEdit"
            form="contractForm"
            :show-draft="activeTemplate?.status !== TEMPLATE_MODES.published"
            :is-submitting="isSubmitting || !canUpdateContractTemplate"
            :is-published="activeTemplate?.status === TEMPLATE_MODES.published"
            :can-delete-roles="
              activeTemplate?.is_lendflow_template
                ? [SUPER_ADMIN, ADMIN]
                : undefined
            "
            @delete="$emit('show-delete-modal')"
            @save-draft="$emit('save-draft')"
            @publish="$emit('publish')"
          >
            <template v-if="lastUpdated" #lastUpdated>
              {{ lastUpdated }}
            </template>
          </template-action-buttons>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";

import { storeToRefs } from "pinia";

import { Ability, PermissionSubject } from "@/enums/auth";
import { ADMIN, SUPER_ADMIN } from "@/helpers/constants";
import { TEMPLATE_MODES } from "@/helpers/constants/contractBuilder";
import { formatDateCustom } from "@/helpers/formatting";
import { usePermissions } from "@/hooks/auth";
import {
  ROUTE_CONTRACT_BUILDER,
  ROUTE_CONTRACT_BUILDER_EDIT,
} from "@/router/routes";
import { useContractBuilderStore } from "@/stores/contractBuilder";

import TemplateActionButtons from "@/components/ui/TemplateActionButtons.vue";
import BackButton from "@/components/ui/buttons/BackButton.vue";
import LfHeading1 from "@/components/ui/text/LfHeading1.vue";

defineEmits<{
  publish: [];
  "save-draft": [];
  "show-delete-modal": [];
}>();

defineProps<{
  isSubmitting: boolean;
}>();

const store = useContractBuilderStore();
const route = useRoute();
const { t } = useI18n();
const { canPerformActionReactive } = usePermissions();

const { activeTemplate } = storeToRefs(store);

const isEdit = computed(() => route.name === ROUTE_CONTRACT_BUILDER_EDIT);

const backRoute = computed(() => ({
  name: ROUTE_CONTRACT_BUILDER,
}));

const lastUpdated = computed(() => {
  if (!activeTemplate.value?.updated_at) {
    return null;
  }
  const date = formatDateCustom(
    activeTemplate.value.updated_at,
    "yyyy-MM-dd HH:mm:ss"
  );
  return `${t("LOGS.LAST_UPDATED")} ${date}`;
});

const canUpdateContractTemplate = canPerformActionReactive(
  PermissionSubject.contractTemplate,
  route.params.templateId ? Ability.update : Ability.create,
  route.params.templateId
    ? {
        template: Number(route.params.templateId),
      }
    : undefined
);
</script>
